import React, { useEffect, useState, useMemo } from "react";
import "./search-tabs.scss";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import ComingSoonPopup from "../../ui/commons/ComingSoonPopup";
import {
  LayoutGroup,
  motion,
  useMotionValue,
} from "framer-motion";
import {get} from '../../../utils/request';
import { Select, Space, Image } from 'antd';
import FlightsSearchTabs from "../../Flights/SearchTabs/FlightsSearchTabs";
import ExploreRoundedIcon from "@mui/icons-material/ExploreRounded";
import { ReactComponent as FlightsIcon } from "../../../assets/Icons/services/flights.svg";
import { ReactComponent as EsimIcon } from "../../../assets/Icons/services/esim.svg";
import { ReactComponent as AirportLoungeIcon } from "../../../assets/Icons/services/airport-lounge.svg";
import { ReactComponent as HotelsIcon } from "../../../assets/Icons/services/hotels.svg";
import useServiceAvailability from "../../../hooks/useServiceAvailability";

const CATEGORIES = [
  { id: "flights", label: "Flights", Icon: FlightsIcon, helper: "Search fares" },
  { id: "esim", label: "eSIMs", Icon: EsimIcon, helper: "Instant travel data" },
  { id: "things", label: "Things To Do", Icon: ExploreRoundedIcon, helper: "Discover experiences" },
  { id: "lounges", label: "Airport Lounge", Icon: AirportLoungeIcon, helper: "Explore lounges" },
  { id: "hotels", label: "Hotels", Icon: HotelsIcon, helper: "Stays coming soon" },
];

const SEGMENTS = [
  { id: "local", label: "Local", helper: "Country plans" },
  { id: "regional", label: "Regional", helper: "Multi-country" },
  { id: "global", label: "Global", helper: "Worldwide" },
];

const EASE = [0.22, 0.8, 0.28, 1];
const DURATION = 0.34;

export default function SearchTabs({flightsLocationSelections, setFlightsLocationSelections}) {
  const navigate = useNavigate();
  const location = useLocation();
  const serviceAvailability = useServiceAvailability();

  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(null);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [locationType, setLocationType] = useState("local");
  const [locationValue, setLocationValue] = useState("Singapore");
  const [busy, setBusy] = useState(false);
  const [loungeCountries, setLoungeCountries] = useState([]);
  const [loungeCountry, setLoungeCountry] = useState();
  const [loungesLoading, setLoungesLoading] = useState(false);

  const [popularLocalLocations, setPopularLocalLocations] = useState([]);
  const [localLocations, setLocalLocations] = useState([]);
  const [popularRegionalLocations, setPopularRegionalLocations] = useState([]);
  const [regionalLocations, setRegionalLocations] = useState([]);

  const visibleCategories = useMemo(() => CATEGORIES.filter((category) => {
    const availabilityKey = {
      things: "thingsToDo",
      lounges: "airportLounge",
    }[category.id] || category.id;
    return serviceAvailability[availabilityKey] !== false;
  }), [serviceAvailability]);



  // const [localLocationOptions, setLocalLocationOptions]  = useState([]);
  // const [regionalLocationOptions, setRegionalLocationOptions] = useState([]);
 
  const br = useMotionValue(28);
  useEffect(() => {
    fetchEsimLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    
  }, [location]);

  useEffect(() => {
    if (location.pathname.startsWith("/esim")) {
      let locationTypeValue = searchParams.get("locationType");
      if (!!locationTypeValue) {
        setLocationType(locationTypeValue);
      }
      let location = searchParams.get("location");
      if (!!location) {
        setLocationValue(location);
      }

      setActiveTab("esim");
    } else if (location.pathname.startsWith("/flights")) {


      setActiveTab("flights");
    } else if (location.pathname.startsWith("/airport-lounges")) {
      setActiveTab("lounges");
    }
  }, [searchParams, location]);


  const handleTabClick = (e, id) => {
    if (id === "esim") {
      e.preventDefault();
      fetchEsimLocations();
      setActiveTab("esim");
      return;
    }
    if (id === "flights") {
      e.preventDefault();
      setActiveTab("flights");
      return;
    }

    if (id === "things") {
      e.preventDefault();
      navigate("/things-to-do");
      return;
    }

    if (id === "hotels") {
      e.preventDefault();
      setComingSoonOpen(true);
      return;
    }

    if (id === "lounges") {
      e.preventDefault();
      setActiveTab("lounges");
      fetchLoungeCountries();
      return;
    }

    e.preventDefault();
    setActiveTab(id);
  };

  const fetchLoungeCountries = async () => {
    if (loungeCountries.length || loungesLoading) return;
    setLoungesLoading(true);
    try {
      const result = await get("/lounge/countries");
      const nextCountries = Array.isArray(result) ? result : [];
      setLoungeCountries(nextCountries);
      setLoungeCountry((current) => current || nextCountries[0]?.countryCode);
    } catch (error) {
      console.error("failed to fetch lounge countries", error);
    } finally {
      setLoungesLoading(false);
    }
  };

  const fetchEsimLocations = async () => {
    try {
      const result = await get("/esim/getLocations");
      setPopularLocalLocations(result.popularLocalLocations);
      setLocalLocations(result.localLocations);
      setPopularRegionalLocations(result.popularRegionalLocations);
      setRegionalLocations(result.regionalLocations);
     
      setDefaultLocationValue(locationType, result.popularLocalLocations, result.localLocations, result.popularRegionalLocations, result.regionalLocations);
    } catch (e) {
      console.error("failed to fetch esim locations", e);
    }
  }

  function resolveLocationCode(value, popularLocations, locations) {
    if (!value) return value;
    const allLocations = [
      ...(popularLocations || []),
      ...(locations || []),
    ];
    const matchedLocation = allLocations.find((location) => {
      return location.code === value || location.name === value;
    });
    return matchedLocation ? matchedLocation.code : value;
  }

  function setDefaultLocationValue(locationType, popularLocalLocations, localLocations, popularRegionalLocations, regionalLocations) {
    if (!!searchParams && searchParams.get("locationType") === locationType) {
      if (locationType === 'global') {
        setLocationValue("");
        return;
      } else if (!!searchParams.get("location")) {
        setLocationValue(
          resolveLocationCode(
            searchParams.get("location"),
            locationType === "local" ? popularLocalLocations : popularRegionalLocations,
            locationType === "local" ? localLocations : regionalLocations
          )
        );
        return;
      }
  
    }
    
    let locationValue;
    if (locationType === 'local') {
      locationValue = !!popularLocalLocations && popularLocalLocations.length > 0 ? popularLocalLocations[0].code
        : !!localLocations && localLocations.length > 0 ? localLocations[0].code : null;
    } else if (locationType === 'regional') {
      locationValue = !!popularRegionalLocations && popularRegionalLocations.length > 0 ? popularRegionalLocations[0].code
      : !!regionalLocations && regionalLocations.length > 0 ? regionalLocations[0].code : null;
    } else if (locationType === 'global') {
      locationValue = "";
    }
    
    setLocationValue(locationValue);
  }

  function buildLocationOptions(popularLocations, locations) {
    if (!popularLocations || popularLocations.length === 0) {
      return toLocationOptions(locations);
    } 

    return [
      {
        label: <span>Popular</span>,
        title: "Popular",
        options: toLocationOptions(popularLocations)
      },
      {
        label: <span>All Locations</span>,
        title: "All Locations",
        options: toLocationOptions(locations)
      },
    ];
  }

  function toLocationOptions(locations) {
    return locations.map(location => {
      return {
        label: (
          <Space className="location-select-option">
            <span role="img" aria-label={location.code}>
              <Image
                className="location-flag"
                src={location.flagUrl}
                alt={location.name}
                preview={false}
                width={25}
              />
            </span>
            <span className="location-label">
              {location.name}
            </span>
            
          </Space>
        ),
        value: location.code,
        flagUrl: location.flagUrl,
        name: location.name,
        code: location.code
      }
    });
  }

  const localLocationOptions = useMemo(
    () => buildLocationOptions(popularLocalLocations, localLocations),
    [popularLocalLocations, localLocations]
  );

  const regionalLocationOptions = useMemo(
    () => buildLocationOptions(popularRegionalLocations, regionalLocations),
    [popularRegionalLocations, regionalLocations]
  );

  const handleFindEsim = async () => {
    if (busy) return;
    setBusy(true);

    const params = new URLSearchParams();
    params.set("locationType", locationType);
    if (locationValue) params.set("location", locationValue);

    navigate(`/esim?${params.toString()}`, {
      state: {
        selectedLocationType: locationType,
        selectedArea: locationValue ? { name: locationValue, value: locationValue } : null,
      },
    });

    setBusy(false);
  };

  const handleFindLounge = () => {
    if (!loungeCountry) return;
    const selectedCountry = loungeCountries.find((country) => country.countryCode === loungeCountry);
    const params = new URLSearchParams({ country: loungeCountry });
    if (selectedCountry?.countryName) params.set("countryName", selectedCountry.countryName);
    navigate(`/airport-lounges/search?${params.toString()}`);
  };

  const contentStagger = {
    hidden: { opacity: 0, y: 8 },
    show: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: 0.08 + i * 0.05, duration: 0.25, ease: EASE },
    }),
  };

  return (
    <div className="container">
      <div
        className="home-search-tabs morph-hero anchored-top"
      >
        <LayoutGroup>
          <motion.div
            layout
            layoutId="tabsShell"
            className="tabs-shell as-card"
            style={{ borderRadius: br, transformOrigin: "top center" }}
            transition={{ layout: { duration: DURATION, ease: EASE } }}
          >
            <div className="search-card-glow" aria-hidden="true" />
            <div className="shell-top" style={{ "--service-tab-count": visibleCategories.length }}>
              {visibleCategories.map((c) => {
                const active = c.id === activeTab;
                const ServiceIcon = c.Icon;
                return (
                  <a
                    key={c.id}
                    href={c.id === "flights" ? "/flights" : c.id === "esim" ? "/esim" : c.id === "things" ? "/things-to-do" : c.id === "lounges" ? "/airport-lounges" : "/hotels"}
                    className={`tab-link ${active ? "active" : ""}`}
                    onClick={(e) => handleTabClick(e, c.id)}
                  >
                    <span className="tab-icon" aria-hidden="true">
                      <ServiceIcon />
                    </span>
                    <span className="tab-copy">
                      <span className="tab-label">{c.label}</span>
                      <span className="tab-helper">{c.helper}</span>
                    </span>
                  </a>
                );
              })}
            </div>

            {activeTab && (
              <>
                <motion.hr
                  className="shell-divider"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, ease: EASE }}
                />
                <motion.div
                  className="shell-inner"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.18, delay: 0.06, ease: EASE }}
                >
              {activeTab === "esim" && (
                <div className="esim-search-panel">
                  <div
                    className="segmented"
                    role="tablist"
                    aria-label="Location type"
                  >
                    {SEGMENTS.map((s, idx) => {
                      const active = s.id === locationType;
                      return (
                        <motion.button
                          key={s.id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          className={`seg-btn ${active ? "active" : ""}`}
                          onClick={() => {
                            setLocationType(s.id);
                            setDefaultLocationValue(
                              s.id,
                              popularLocalLocations,
                              localLocations,
                              popularRegionalLocations,
                              regionalLocations
                            );
                          }}
                          custom={idx}
                          variants={contentStagger}
                          initial="hidden"
                          animate="show"
                          whileHover={{ y: -1 }}
                        >
                          <span>{s.label}</span>
                          <small>{s.helper}</small>
                        </motion.button>
                      );
                    })}
                  </div>

                  <div className="form-row">
                    <motion.div
                      className="form-col"
                      custom={0}
                      variants={contentStagger}
                      initial="hidden"
                      animate="show"
                    >
                      <label className="form-label">Destination</label>

                      {locationType === "local" && (
                        <div
                          className="field-card"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Select
                            placeholder="Select a Country or Region"
                            className="location-select"
                            showSearch
                            optionFilterProp="name"
                            style={{ width: "100%", height: 50 }}
                            value={locationValue}
                            onChange={(value) => setLocationValue(value)}
                            options={localLocationOptions}
                          />
                        </div>
                      )}

                      {locationType === "regional" && (
                        <div
                          className="field-card"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Select
                            placeholder="Select a Region & Country"
                            className="location-select"
                            showSearch
                            optionFilterProp="name"
                            style={{ width: "100%", height: 50 }}
                            value={locationValue}
                            onChange={(value) => setLocationValue(value)}
                            options={regionalLocationOptions}
                          />
                        </div>
                      )}

                      {locationType === "global" && (
                        <div className="select-wrap disabled">
                          <span className="select-emoji">🌍</span>
                          <input value="All countries" readOnly />
                        </div>
                      )}
                    </motion.div>

                    <motion.div
                      className="form-col cta-col"
                      custom={1}
                      variants={contentStagger}
                      initial="hidden"
                      animate="show"
                    >
                      <motion.button
                        type="button"
                        className="btn btn-warning find-btn"
                        onClick={handleFindEsim}
                        disabled={busy}
                        whileHover={{ scale: busy ? 1 : 1.02 }}
                        whileTap={{ scale: busy ? 1 : 0.98 }}
                      >
                        <span>{busy ? "Loading..." : "Find eSIM"}</span>
                        <small>Compare live plans</small>
                      </motion.button>
                    </motion.div>
                  </div>
                </div>
              )}

              {activeTab === "flights" && <div className="flights-search-panel"><FlightsSearchTabs flightsLocationSelections={flightsLocationSelections} setFlightsLocationSelections={setFlightsLocationSelections}/></div>}
              {activeTab === "lounges" && (
                <div className="lounge-search-panel">
                  <div className="form-row">
                    <div className="form-col">
                      <label className="form-label" htmlFor="home-lounge-country">Country</label>
                      <div className="field-card">
                        <Select
                          id="home-lounge-country"
                          aria-label="Airport lounge country"
                          className="location-select"
                          placeholder={loungesLoading ? "Loading countries…" : "Select a country"}
                          showSearch
                          optionFilterProp="label"
                          value={loungeCountry}
                          onChange={setLoungeCountry}
                          loading={loungesLoading}
                          options={loungeCountries.map((country) => ({
                            value: country.countryCode,
                            label: `${country.flag || ""} ${country.countryName}`.trim(),
                          }))}
                        />
                      </div>
                    </div>
                    <div className="form-col cta-col">
                      <button
                        type="button"
                        className="btn btn-warning find-btn"
                        onClick={handleFindLounge}
                        disabled={!loungeCountry || loungesLoading}
                      >
                        <span>Find lounges</span>
                        <small>View live locations</small>
                      </button>
                    </div>
                  </div>
                </div>
              )}
                </motion.div>
              </>
            )}
          </motion.div>
        </LayoutGroup>
      </div>
      <ComingSoonPopup
        isOpen={comingSoonOpen}
        onOk={() => setComingSoonOpen(false)}
        onCancel={() => setComingSoonOpen(false)}
      />
    </div>
  );
}
